import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  structureBytes,
  STRUCTURE_WARN_BYTES,
  validateScreens,
  validateStructureSize,
  type Quiz,
  type Screen,
  type Theme,
} from '@floorpoll/shared';
import { CodeCells } from '../../components/CodeCells.js';
import { FormError, FormNotice } from '../../components/Form.js';
import { ApiError } from '../../lib/api.js';
import { describeIssue } from '../../lib/issuePath.js';
import { quizApi } from '../../lib/quizApi.js';
import { track } from '../../lib/track.js';
import { useTourAnchor } from '../../lib/tour/anchors.js';
import { useWorkspace } from '../../lib/workspace.js';
import { canMoveQuestion, moveQuestion } from './questionMoves.js';
import { RunningOrder } from './RunningOrder.js';
import { ScreenEditor, emptyScreen } from './ScreenEditor.js';
import { ThemeEditor } from './ThemeEditor.js';
import { hasUnsavedChanges } from './unsavedChanges.js';

/** Anchor for the running order's jump to branding, which sits below the poll. */
const BRANDING_ID = 'poll-branding';

/** Stories #14–#19 — the builder. */
export function QuizEditor() {
  const { quizId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { canIn } = useWorkspace();

  // Story #48 — set by QuizList when it sends a fresh copy here. The original's
  // title, not the copy's: the notice names where this came from.
  const duplicatedFrom = (location.state as { duplicatedFrom?: string } | null)?.duplicatedFrom;

  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [title, setTitle] = useState('');
  const [screens, setScreens] = useState<Screen[]>([]);
  const [theme, setTheme] = useState<Theme | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [movedQuestion, setMovedQuestion] = useState<{
    questionId: string;
    direction: -1 | 1;
  } | null>(null);

  // Before the early returns below, and before the ternaries these sit in.
  const headingAnchor = useTourAnchor('poll-heading');
  const addScreenAnchor = useTourAnchor('poll-add-screen');
  const saveAnchor = useTourAnchor('poll-save');
  const presentAnchor = useTourAnchor('poll-present');

  // A question crossing to another screen is unmounted and remounted under a
  // different ScreenEditor, so React cannot carry focus with it the way it does
  // for a step inside one screen — the arrow just pressed stops existing. Put
  // the caret back so a second press lands where the author expects. A step
  // that stayed inside a screen re-focuses the element that already has focus,
  // which costs nothing and keeps the caller free of the distinction.
  //
  // Layout effect, not passive: focusing the arrow scrolls it into view, and a
  // passive effect can let that scroll land after a paint at the old position.
  useLayoutEffect(() => {
    if (!movedQuestion) return;
    // Not needed to stop a re-fire — each press allocates a fresh object, so the
    // dependency changes anyway. It keeps the signal from outliving the commit
    // that consumed it, so an unrelated re-render cannot resurrect a stale move.
    setMovedQuestion(null);

    const { questionId, direction } = movedQuestion;
    const arrow = document.getElementById(
      `move-${questionId}-${direction === -1 ? 'earlier' : 'later'}`,
    );
    if (arrow instanceof HTMLButtonElement && !arrow.disabled) {
      arrow.focus();
      return;
    }
    // The question landed at an end of the poll, so that arrow is now disabled.
    document.getElementById(`q-${questionId}`)?.focus();
  }, [movedQuestion]);

  // Permissions come from the quiz's own workspace, not the active one — a
  // quiz opened by URL may belong to a workspace where this user is a viewer.
  const can = (capability: Parameters<typeof canIn>[1]) => canIn(quiz?.workspaceId, capability);
  const canWrite = can('quiz:write');
  // While live, everything structural is frozen (§5).
  const locked = !canWrite || quiz?.status === 'live';

  // `quiz` holds the last thing the server accepted, so the three pieces of
  // editable state are compared straight against it. Field by field rather than
  // by serialising the two — see unsavedChanges.ts for the round trips a
  // stringify calls a change.
  const dirty = Boolean(quiz && theme && hasUnsavedChanges(quiz, title, screens, theme));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const loaded = await quizApi.get(quizId);
      // On a successful load, not on the click that navigated here: a poll
      // that failed to open was not opened.
      track('pollOpened');
      setQuiz(loaded);
      setTitle(loaded.title);
      setScreens(loaded.screens);
      setTheme(loaded.theme);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load that poll.');
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  useEffect(() => {
    void load();
  }, [load]);

  // A copy arrives with a placeholder title, so the first thing it does is
  // offer it up selected: type to rename, tab away to keep it (#48). Not
  // `autoFocus` on the input — the field renders before the poll has loaded,
  // and its value would arrive after the selection.
  //
  // Once only. Every later render of this page — a save, a screen edit — would
  // otherwise drag the caret back out of whatever the author is typing in.
  const greeted = useRef(false);
  useEffect(() => {
    if (!quiz || !duplicatedFrom || greeted.current) return;
    greeted.current = true;

    setNotice(`Duplicated from “${duplicatedFrom}”. New join code, and no votes carried over.`);

    const field = document.getElementById('quiz-title');
    if (field instanceof HTMLInputElement) {
      field.focus();
      field.select();
    }
  }, [quiz, duplicatedFrom]);

  async function save() {
    if (!quiz || !theme) return;
    setError(null);
    setNotice(null);

    const issues = [...validateScreens(screens), ...validateStructureSize(screens, theme)];
    if (issues.length > 0 && issues[0]) {
      setError(describeIssue(issues[0]));
      return;
    }

    // Compared against the loaded poll before the save overwrites it. Counted
    // here rather than in ThemeEditor because that component's onChange fires
    // on every keystroke in a color field — one deliberate act, one count.
    const themeChanged = JSON.stringify(theme) !== JSON.stringify(quiz.theme);

    setSaving(true);
    try {
      await quizApi.update(quiz.quizId, { title: title.trim(), theme });
      const updated = await quizApi.saveStructure(quiz.quizId, screens);
      track('pollEdited');
      if (themeChanged) track('themeEdited');
      setQuiz(updated);
      setNotice('Saved.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save your changes.');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!quiz) return;
    if (
      !confirm(`Delete "${quiz.title}"? Its questions and results go too. This cannot be undone.`)
    )
      return;

    try {
      await quizApi.remove(quiz.quizId);
      navigate('/polls');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not delete that poll.');
    }
  }

  if (loading) return <p className="muted">Loading…</p>;
  if (!quiz || !theme) {
    return (
      <section>
        <FormError message={error ?? 'Poll not found.'} />
        <Link to="/polls">Back to polls</Link>
      </section>
    );
  }

  return (
    <section className="builder">
      <div className="page-head">
        <div ref={headingAnchor}>
          <Link to="/polls" className="muted">
            ← All polls
          </Link>
          <h1>{quiz.title}</h1>
          <p className="muted join-line">
            {/* Hidden from the accessibility tree: CodeCells already announces
                itself as "Join code A B C 2 3 4", so leaving this readable
                would say the words twice. */}
            <span aria-hidden="true">Join code</span>
            {/* A copy's one visible difference from its original is this code,
                so it flaps over as the page opens — the same departure-board
                move the big screen makes when a code first goes up. */}
            <CodeCells code={quiz.joinCode} size="sm" flip={Boolean(duplicatedFrom)} />
            {quiz.status === 'live' ? <span>· presenting now</span> : null}
          </p>
          {quiz.sample ? (
            <p className="muted sample-line">
              {can('quiz:write')
                ? 'A sample poll: present it as it is, or change anything and save. To keep this version too, duplicate it from Polls first.'
                : 'A sample poll, showing what FloorPoll can do.'}
            </p>
          ) : null}
        </div>
        <div className="page-head-actions">
          <Link className="button secondary" to={`/polls/${quiz.quizId}/results`}>
            Results
          </Link>
          {can('quiz:present') ? (
            <Link ref={presentAnchor} className="button" to={`/polls/${quiz.quizId}/present`}>
              {quiz.status === 'live' ? 'Open controls' : 'Present'}
            </Link>
          ) : null}
        </div>
      </div>

      {quiz.status === 'live' ? (
        <p className="form-notice" role="status">
          This poll is being presented. Editing is paused until you end the presentation.
        </p>
      ) : null}

      <FormError message={error} />
      <FormNotice message={notice} />

      <div className="builder-body">
        {/* Index, not a second editor. Every screen stays mounted in the canvas
            beside it: a question can step into a neighbouring screen, and the
            author has to watch it land. */}
        <aside className="builder-rail">
          <RunningOrder screens={screens} />
          <a className="rail-jump" href={`#${BRANDING_ID}`}>
            Branding
          </a>
        </aside>

        <div className="builder-canvas">
          <div className="title-field field">
            <label htmlFor="quiz-title">Title</label>
            <input
              id="quiz-title"
              value={title}
              disabled={locked}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {screens.map((screen, index) => (
            <ScreenEditor
              key={screen.screenId}
              screen={screen}
              quizId={quizId}
              index={index}
              total={screens.length}
              disabled={locked}
              onChange={(next) => setScreens(screens.map((s, i) => (i === index ? next : s)))}
              onMove={(direction) => {
                const target = index + direction;
                if (target < 0 || target >= screens.length) return;
                const next = [...screens];
                const [moved] = next.splice(index, 1);
                if (moved) next.splice(target, 0, moved);
                setScreens(next);
              }}
              onRemove={() => setScreens(screens.filter((_, i) => i !== index))}
              canMoveQuestion={(questionIndex, direction) =>
                canMoveQuestion(screens, index, questionIndex, direction)
              }
              onMoveQuestion={(questionIndex, direction) => {
                const question = screens[index]?.questions[questionIndex];
                const next = moveQuestion(screens, index, questionIndex, direction);
                if (!question || next === screens) return;
                setScreens(next);
                setMovedQuestion({ questionId: question.questionId, direction });
              }}
            />
          ))}

          <button
            ref={addScreenAnchor}
            type="button"
            className="ghost-add ghost-add-screen"
            disabled={locked}
            onClick={() => setScreens([...screens, emptyScreen()])}
          >
            Add screen
          </button>

          <div id={BRANDING_ID}>
            <ThemeEditor quizId={quiz.quizId} theme={theme} disabled={locked} onChange={setTheme} />
          </div>

          {structureBytes(screens, theme) > STRUCTURE_WARN_BYTES ? (
            <p className="form-warning" role="status">
              This poll is getting close to the size limit. Shortening follow-up messages, or
              splitting it into two polls, will keep it saveable.
            </p>
          ) : null}
        </div>
      </div>

      <div className="sticky-actions">
        <button
          ref={saveAnchor}
          type="button"
          onClick={() => void save()}
          disabled={locked || saving}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {/* Says which of the two reasons the button is quiet, so a disabled
            Save is never mistaken for nothing left to do. */}
        <p className="save-state muted" role="status">
          {locked ? null : dirty ? 'Unsaved changes' : 'Everything saved'}
        </p>
        {can('quiz:delete') ? (
          <button
            type="button"
            className="secondary quiet-danger"
            disabled={quiz.status === 'live'}
            onClick={() => void remove()}
          >
            Delete poll
          </button>
        ) : null}
      </div>
    </section>
  );
}
